import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useProductsContext } from '../../context/products_context';
import { useCartContext } from '../../context/cart_context';
import { useLanguageContext } from '../../context/language_context';
import { useUserContext } from '../../context/user_context';
import { useOrderContext } from '../../context/order_context';
import { single_product_url as url, create_order_url } from '../../utils/constants';
import { formatPrice, isArabicText, getShippingCost } from '../../utils/helpers';
import { setPixelUserData, buildContents, sumItems, metaOrderFields } from '../../utils/metaPixel';
import {
  Loading,
  Error,
  ReviewModal,
} from '../../components';
import SEOHead from '../../components/SEOHead/';
import { ProductJsonLd, BreadcrumbJsonLd } from '../../components/SEOHead/JsonLd';
import { getProductSEO } from '../../utils/seo';
import Wrapper from './styles';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import emailjs from '@emailjs/browser';
import {
  FaCheck,
  FaStar,
  FaStarHalfAlt,
  FaRegStar,
  FaTruck,
  FaShieldAlt,
  FaHandHoldingUsd,
  FaAward,
  FaHeadset,
  FaUndo,
  FaQuoteRight,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaSearchPlus
} from 'react-icons/fa';

const SingleProductPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const { t, isArabic } = useLanguageContext();
  const { currentUser } = useUserContext();
  const { fetchOrders } = useOrderContext();
  const {
    single_product_loading: loading,
    single_product_error: error,
    single_product: product,
    fetchSingleProduct,
  } = useProductsContext();
  const { addToCart } = useCartContext();

  const [variantSelections, setVariantSelections] = useState({});
  const [amount, setAmount] = useState(1);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);
  const [mainImage, setMainImage] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Customer info form state
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    governorate: '',
    city: '',
  });

  // Benefits data
  const benefits = [
    {
      icon: FaTruck,
      title: isArabic ? 'توصيل سريع' : 'Livraison Rapide'
    },
    {
      icon: FaShieldAlt,
      title: isArabic ? 'جودة مضمونة' : 'Qualité Garantie'
    },
    {
      icon: FaHandHoldingUsd,
      title: isArabic ? 'الدفع عند الاستلام' : 'Paiement à la livraison'
    },
  ];

  // Why choose us data
  const whyChooseReasons = [
    {
      icon: FaAward,
      title: isArabic ? 'منتجات مختارة بعناية' : 'Sélection de Qualité',
      description: isArabic
        ? 'كل منتج يتم اختياره بعناية لجودته وتصميمه.'
        : 'Chaque produit est soigneusement sélectionné pour sa qualité et son design.'
    },
    {
      icon: FaHeadset,
      title: isArabic ? 'دعم محلي' : 'Support Local Tunisien',
      description: isArabic
        ? 'فريقنا موجود في تونس لمساعدتك في كل خطوة.'
        : 'Notre équipe est basée en Tunisie pour vous accompagner à chaque étape.'
    },
    {
      icon: FaUndo,
      title: isArabic ? 'إرجاع سهل' : 'Retours Sans Souci',
      description: isArabic
        ? 'غير راضٍ؟ يمكنك إرجاع المنتج بسهولة خلال 14 يومًا.'
        : 'Pas satisfait? Retournez votre produit facilement sous 14 jours.'
    },
  ];

  useEffect(() => {
    fetchSingleProduct(`${url}${id}`);
    // Reset per-product state when navigating to a different product
    setMainImage(null);
    setSelectedOffer(null);
    setVariantSelections({});
    setAmount(1);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        history.push('/');
      }, 3000);
    }
    // eslint-disable-next-line
  }, [error]);

  const {
    name,
    price,
    originalPrice,
    discountPercentage,
    isOffer,
    description,
    stock,
    rating: stars,
    numberOfReviews,
    reviews = [],
    images,
    videos = [],
    colors = [],
    sizes = [],
    variants = [],
    media = [],
    offers = [],
  } = product;

  // True only once the product in context matches the current route id. When
  // navigating from one product to another, context still holds the PREVIOUS
  // product until its refetch resolves — deriving the color selection or image
  // from that stale product is what showed the wrong image/color until a
  // refresh. Gate per-product initialization on this.
  const productLoaded = product && String(product._id) === String(id);

  // Auto-select first offer when offers are available
  useEffect(() => {
    if (offers && offers.length > 0 && selectedOffer === null) {
      setSelectedOffer(0);
    }
  }, [offers, selectedOffer]);

  // Build ordered gallery from media array (if available) or fallback to images+videos
  const galleryMedia = useMemo(() => {
    if (media && media.length > 0) return media;
    return [
      ...(images || []).map((img) => ({ type: 'image', url: img.url, public_id: img.public_id })),
      ...(videos || []).map((vid) => ({ type: 'video', url: vid.url, public_id: vid.public_id })),
    ];
  }, [media, images, videos]);

  // Normalize colors: support both {name,hex} objects and plain hex strings
  const getColorHex = (color) =>
    typeof color === 'object' && color !== null ? color.hex : color;
  const getColorName = (color) =>
    typeof color === 'object' && color !== null ? color.name : color;

  // A variant value is out of stock only when its stock is explicitly set to 0
  // (or negative). Empty/undefined stock means "not tracked" → always available.
  const isValueOutOfStock = (val) => {
    if (typeof val !== 'object' || val === null) return false;
    if (val.stock === undefined || val.stock === null || val.stock === '') return false;
    return Number(val.stock) <= 0;
  };

  // Build effective variants: use product.variants if available, else fall back to old colors/sizes
  const effectiveVariants = useMemo(() => {
    if (variants && variants.length > 0) return variants;
    const fallback = [];
    if (colors.length > 0) {
      fallback.push({ name: isArabic ? 'اللون' : 'Couleur', type: 'color_buttons', values: colors });
    }
    if (sizes.length > 0) {
      fallback.push({ name: isArabic ? 'المقاس' : 'Taille', type: 'textual_buttons', values: sizes });
    }
    return fallback;
  }, [variants, colors, sizes, isArabic]);

  // Helper to get display value of a variant selection
  const getVariantDisplay = (val) =>
    typeof val === 'object' && val !== null ? (val.name || val.hex || String(val)) : (val || '');

  const itemQty = useMemo(() => {
    const hasOffers = offers && offers.length > 0;
    const activeOffer = hasOffers && selectedOffer !== null ? offers[selectedOffer] : null;
    return activeOffer && activeOffer.quantity ? activeOffer.quantity : 1;
  }, [offers, selectedOffer]);

  // The image the page should show automatically: the selected variant value's
  // linked image (imageIndex set in admin) if it has one, otherwise the first
  // gallery item. Derived as a single value so the initial image is
  // deterministic — a single effect writes it, avoiding the earlier race where
  // two effects fought over the main image and the default sometimes won.
  const autoMainImage = useMemo(() => {
    if (!productLoaded) return null;
    const toMain = (item) =>
      item && item.url
        ? (item.type === 'video' ? { url: item.url, isVideo: true } : { url: item.url })
        : null;
    const linked = Object.values(variantSelections).find(
      (v) => v && typeof v === 'object' && v.imageIndex !== undefined && v.imageIndex !== null && v.imageIndex !== ''
    );
    if (linked) {
      const fromVariant = toMain(galleryMedia[Number(linked.imageIndex)]);
      if (fromVariant) return fromVariant;
    }
    return galleryMedia.length > 0 ? toMain(galleryMedia[0]) : null;
  }, [productLoaded, variantSelections, galleryMedia]);

  // Set default variant selections when the CURRENT product has loaded.
  useEffect(() => {
    if (productLoaded && effectiveVariants.length > 0) {
      setVariantSelections((prev) => {
        const defaults = { ...prev };
        let changed = false;
        
        for (let i = 0; i < itemQty; i++) {
          effectiveVariants.forEach((v) => {
            const fieldName = itemQty > 1 ? `${v.name} (#${i + 1})` : v.name;
            if (v.values && v.values.length > 0 && defaults[fieldName] === undefined) {
              const firstAvailable = v.values.find((val) => !isValueOutOfStock(val));
              defaults[fieldName] = firstAvailable !== undefined ? firstAvailable : v.values[0];
              changed = true;
            }
          });
        }
        
        return changed ? defaults : prev;
      });
    }
  }, [productLoaded, effectiveVariants, itemQty]);

  // Single writer for the automatic main image (initial load + on color change).
  // `autoMainImage` is memoized, so this only fires when the selection or the
  // gallery actually changes — manual thumbnail clicks are preserved.
  useEffect(() => {
    if (autoMainImage) setMainImage(autoMainImage);
  }, [autoMainImage]);

  useEffect(() => {
    document.title = `Darista | ${name}`;

    // Meta Pixel - Track ViewContent.
    // Use displayPrice (the price actually shown, after variant override) so
    // ViewContent and Purchase don't report different prices for the product.
    if (window.fbq && product) {
      window.fbq('track', 'ViewContent', {
        content_name: name,
        content_ids: [product._id],
        content_type: 'product',
        value: Number(displayPrice) || Number(price) || 0,
        currency: 'TND',
      });
    }
    // displayPrice intentionally omitted from deps: ViewContent should fire
    // once per product view, not on every variant change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, product, price]);

  // Compute variant-specific price override if any
  const variantPriceOverride = useMemo(() => {
    let override = null;
    if (variantSelections) {
      Object.values(variantSelections).forEach((val) => {
        if (typeof val === 'object' && val !== null && val.price !== undefined && val.price !== '') {
          const p = Number(val.price);
          if (!isNaN(p)) {
            override = p;
          }
        }
      });
    }
    return override;
  }, [variantSelections]);

  const displayPrice = variantPriceOverride !== null ? variantPriceOverride : price;

  // Compute effective price based on selected offer
  const hasOffers = offers && offers.length > 0;
  const activeOffer = hasOffers && selectedOffer !== null ? offers[selectedOffer] : null;
  const effectivePrice = activeOffer ? activeOffer.price : displayPrice;

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
  };

  // Function to send order notification email to admin
  const sendOrderNotificationEmail = async (orderData, orderItems, shippingInfo, orderBody) => {
    const serviceId = 'service_ofhnwke';
    const templateId = 'template_order_notificat';
    const publicKey = '0tAEy-0sPHYodpkGO';

    // Format order items for email
    const itemsList = orderItems.map((item, index) => {
      const variantStr = item.variantSelections && Object.keys(item.variantSelections).length > 0
        ? Object.entries(item.variantSelections).map(([k, v]) => `${k}: ${typeof v === 'object' ? v.name : v}`).join(' | ')
        : `Color: ${item.color || 'N/A'} | Size: ${item.size || 'N/A'}`;
      return `${index + 1}. ${item.name}\n   ${variantStr}\n   Quantity: ${item.quantity} x ${formatPrice(item.price)} = ${formatPrice(item.quantity * item.price)}`;
    }).join('\n\n');

    const templateParams = {
      order_id: orderData._id || orderData.id || 'N/A',
      order_date: new Date().toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short'
      }),
      customer_name: orderBody.name,
      customer_email: orderBody.email,
      customer_phone: shippingInfo.phoneNumber,
      shipping_address: `${shippingInfo.address}, ${shippingInfo.city}, ${shippingInfo.state}, ${shippingInfo.country} ${shippingInfo.pinCode}`,
      order_items: itemsList,
      items_count: orderItems.length,
      subtotal: formatPrice(orderBody.itemsPrice),
      shipping_fee: formatPrice(orderBody.shippingPrice),
      total_amount: formatPrice(orderBody.totalPrice),
      payment_method: orderBody.paymentInfo.status,
      admin_email: 'abdelhakcharrada2000@gmail.com',
    };

    try {
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
    } catch (error) {
      // Don't fail the order if email fails
    }
  };

  const handleCashOnDelivery = async () => {
    // Validation
    if (!customerInfo.firstName || !customerInfo.lastName ||
      !customerInfo.phone || customerInfo.phone.length < 8 ||
      !customerInfo.governorate || !customerInfo.city) {
      return toast.error(t.toasts.fillAllFields);
    }

    setOrderLoading(true);

    const shippingInfo = {
      address: customerInfo.city,
      city: customerInfo.city,
      state: customerInfo.governorate,
      country: 'TN',
      pinCode: '0000',
      phoneNumber: customerInfo.phone,
    };

    const orderItems = [
      {
        name: product.name + (activeOffer ? ` — ${activeOffer.description}` : ''),
        price: effectivePrice,
        quantity: amount,
        image: product.images[0]?.url || '',
        color: getVariantDisplay(variantSelections[Object.keys(variantSelections).find(k => k.toLowerCase().includes('couleur') || k.toLowerCase().includes('color') || k.toLowerCase().includes('لون'))] || ''),
        size: getVariantDisplay(variantSelections[Object.keys(variantSelections).find(k => k.toLowerCase().includes('taille') || k.toLowerCase().includes('size') || k.toLowerCase().includes('مقاس'))] || ''),
        variantSelections: Object.fromEntries(
          Object.entries(variantSelections).map(([k, v]) => [k, getVariantDisplay(v)])
        ),
        selectedOffer: activeOffer ? { description: activeOffer.description, price: activeOffer.price, shippingPrice: activeOffer.shippingPrice } : undefined,
        product: product._id,
      },
    ];

    const paymentInfo = {
      id: 'COD_' + Date.now(),
      status: 'Cash on Delivery',
    };

    const itemsPrice = effectivePrice * amount;
    const productShippingCost = getShippingCost(product.shipping);
    const shippingPrice = activeOffer ? activeOffer.shippingPrice : ((productShippingCost && itemsPrice < 200) ? productShippingCost : 0);
    const totalPrice = itemsPrice + shippingPrice;
    const eventId = "order_" + Date.now();

    const body = {
      name: `${customerInfo.firstName} ${customerInfo.lastName}`,
      email: currentUser ? currentUser.email : `guest_${Date.now()}@cashondelivery.com`,
      shippingInfo,
      orderItems,
      paymentInfo,
      itemsPrice,
      shippingPrice: shippingPrice,
      totalPrice: totalPrice,
      eventId, // Pass eventId to backend for deduplication
      ...metaOrderFields(), // fbp/fbc/eventSourceUrl for the Conversions API
    };

    try {
      const response = await axios.post(create_order_url, body);
      const orderData = response.data.data || response.data;

      // Send email notification to admin
      await sendOrderNotificationEmail(orderData, orderItems, shippingInfo, body);

      // Meta Pixel - Track Purchase (with Advanced Matching + per-item prices)
      if (window.fbq) {
        setPixelUserData({
          name: `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim(),
          phone: customerInfo.phone,
          city: customerInfo.city,
          state: customerInfo.governorate,
        });
        const contents = buildContents(
          orderItems.map((i) => ({ id: product._id, price: i.price, quantity: i.quantity }))
        );
        window.fbq('track', 'Purchase', {
          value: Number(totalPrice) || 0,
          currency: 'TND',
          content_ids: contents.map((c) => c.id),
          content_type: 'product',
          contents,
          num_items: sumItems(contents),
          order_id: orderData?._id || orderData?.id,
        }, {
          eventID: eventId
        });
      }

      toast.success(t.toasts.orderSuccess);
      if (currentUser) {
        await fetchOrders();
      }

      setTimeout(() => {
        setOrderLoading(false);
        setCustomerInfo({
          firstName: '',
          lastName: '',
          phone: '',
          governorate: '',
          city: '',
        });
        setAmount(1);
        history.push(currentUser ? '/orders' : '/products');
      }, 3000);
    } catch (error) {
      toast.error(t.toasts.orderError);
      setOrderLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Extract color/size for backward compat
    const colorKey = Object.keys(variantSelections).find(k => k.toLowerCase().includes('couleur') || k.toLowerCase().includes('color') || k.toLowerCase().includes('لون'));
    const sizeKey = Object.keys(variantSelections).find(k => k.toLowerCase().includes('taille') || k.toLowerCase().includes('size') || k.toLowerCase().includes('مقاس'));
    const colorStr = getVariantDisplay(variantSelections[colorKey] || '');
    const sizeStr = getVariantDisplay(variantSelections[sizeKey] || '');

    // addToCart() fires the AddToCart pixel event itself (single source of
    // truth), so we don't fire another one here — that double-counted it.
    addToCart(product._id, colorStr, sizeStr, amount, product, variantSelections);

    history.push('/cart');
  };

  const increase = () => {
    setAmount((oldAmount) => {
      let tempAmount = oldAmount + 1;
      if (tempAmount > stock) {
        tempAmount = stock;
      }
      return tempAmount;
    });
  };

  const decrease = () => {
    setAmount((oldAmount) => {
      let tempAmount = oldAmount - 1;
      if (tempAmount < 1) {
        tempAmount = 1;
      }
      return tempAmount;
    });
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error />;
  }

  // Render stars helper
  const renderStars = (rating) => {
    const starsArr = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      starsArr.push(<FaStar key={`full-${i}`} />);
    }
    if (hasHalfStar) {
      starsArr.push(<FaStarHalfAlt key="half" />);
    }
    const emptyStars = 5 - starsArr.length;
    for (let i = 0; i < emptyStars; i++) {
      starsArr.push(<FaRegStar key={`empty-${i}`} />);
    }
    return starsArr;
  };

  // Check if color is light
  const isLightColor = (color) => {
    if (!color) return false;
    const lightColors = ['#ffffff', '#fff', 'white', '#f5f5f0', '#fffff0', '#fafafa', 'beige', '#f5f5dc'];
    return lightColors.includes(color.toLowerCase());
  };

  const renderVariantSelection = (qty, isInsideOffer = false) => {
    return (
      <div className={`variants-container ${isInsideOffer ? 'inside-offer' : ''}`}>
        {Array.from({ length: qty }).map((_, itemIndex) => (
          <div key={itemIndex} className={`multi-item-variants ${isInsideOffer ? 'compact-row' : ''}`}>
            {qty > 1 && (
              <h4 className="variant-item-label">
                #{itemIndex + 1}
              </h4>
            )}
            <div className={`variant-fields-wrapper ${isInsideOffer ? 'flex-wrapper' : ''}`}>
              {effectiveVariants.map((variant, vIdx) => {
                const fieldName = qty > 1 ? `${variant.name} (#${itemIndex + 1})` : variant.name;
                const selected = variantSelections[fieldName];

                if (isInsideOffer) {
                  // Inside an offer, we want a more compact look as requested
                  if (variant.type === 'color_buttons') {
                    return (
                      <div className='compact-option-group' key={vIdx}>
                        <div className='compact-colors'>
                          {variant.values.map((color, index) => {
                            const soldOut = isValueOutOfStock(color);
                            return (
                              <button
                                key={index}
                                type='button'
                                disabled={soldOut}
                                aria-disabled={soldOut}
                                className={`color-btn-small ${selected === color ? 'active' : ''} ${isLightColor(getColorHex(color)) ? 'light-color' : ''} ${soldOut ? 'out-of-stock' : ''}`}
                                style={{ background: getColorHex(color) }}
                                onClick={(e) => { e.preventDefault(); if (!soldOut) setVariantSelections((prev) => ({ ...prev, [fieldName]: color })); }}
                                title={soldOut ? (isArabic ? 'غير متوفر' : 'Rupture de stock') : getColorName(color)}
                              >
                                {selected === color && <FaCheck />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  // Default to select dropdown for inline compact variants
                  return (
                    <div className='compact-option-group' key={vIdx}>
                      <select
                        className='compact-variant-dropdown'
                        value={variant.values.indexOf(selected)}
                        onChange={(e) => {
                          const val = variant.values[parseInt(e.target.value, 10)];
                          setVariantSelections((prev) => ({ ...prev, [fieldName]: val }));
                        }}
                      >
                        <option value="-1" disabled>{variant.name}</option>
                        {variant.values.map((val, index) => (
                          <option key={index} value={index}>
                            {typeof val === 'object' ? val.name : val}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                // Normal View (Outside offers or standard)
                if (variant.type === 'color_buttons') {
                  return (
                    <div className='option-group' key={vIdx}>
                      <span className='option-label'>
                        {variant.name}: <span>{getColorName(selected)}</span>
                      </span>
                      <div className='colors'>
                        {variant.values.map((color, index) => {
                          const soldOut = isValueOutOfStock(color);
                          return (
                            <button
                              key={index}
                              type='button'
                              disabled={soldOut}
                              aria-disabled={soldOut}
                              title={soldOut ? (isArabic ? 'غير متوفر' : 'Rupture de stock') : getColorName(color)}
                              className={`color-btn ${selected === color ? 'active' : ''} ${isLightColor(getColorHex(color)) ? 'light-color' : ''} ${soldOut ? 'out-of-stock' : ''}`}
                              style={{ background: getColorHex(color) }}
                              onClick={() => { if (!soldOut) setVariantSelections((prev) => ({ ...prev, [fieldName]: color })); }}
                            >
                              {selected === color && <FaCheck />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (variant.type === 'textual_buttons' || variant.type === 'image_buttons') {
                  return (
                    <div className='option-group' key={vIdx}>
                      <span className='option-label'>
                        {variant.name}: <span>{getVariantDisplay(selected)}</span>
                      </span>
                      <div className='variant-buttons'>
                        {variant.values.map((val, index) => {
                          const soldOut = isValueOutOfStock(val);
                          return (
                            <button
                              key={index}
                              type='button'
                              disabled={soldOut}
                              aria-disabled={soldOut}
                              title={soldOut ? (isArabic ? 'غير متوفر' : 'Rupture de stock') : undefined}
                              className={`variant-btn ${selected === val ? 'active' : ''} ${soldOut ? 'out-of-stock' : ''}`}
                              onClick={() => { if (!soldOut) setVariantSelections((prev) => ({ ...prev, [fieldName]: val })); }}
                            >
                              {typeof val === 'object' ? val.name : val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                if (variant.type === 'dropdown') {
                  return (
                    <div className='option-group' key={vIdx}>
                      <span className='option-label'>{variant.name}</span>
                      <select
                        className='variant-dropdown'
                        value={variant.values.indexOf(selected)}
                        onChange={(e) => {
                          const val = variant.values[parseInt(e.target.value, 10)];
                          setVariantSelections((prev) => ({ ...prev, [fieldName]: val }));
                        }}
                      >
                        {variant.values.map((val, index) => (
                          <option key={index} value={index}>
                            {typeof val === 'object' ? val.name : val}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                }

                if (variant.type === 'radio_buttons') {
                  return (
                    <div className='option-group' key={vIdx}>
                      <span className='option-label'>{variant.name}</span>
                      <div className='variant-radios'>
                        {variant.values.map((val, index) => (
                          <label key={index} className={`variant-radio ${selected === val ? 'active' : ''}`}>
                            <input
                              type='radio'
                              name={`variant-${fieldName}`}
                              checked={selected === val}
                              onChange={() => setVariantSelections((prev) => ({ ...prev, [fieldName]: val }))}
                            />
                            <span>{typeof val === 'object' ? val.name : val}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Fallback: render as textual buttons
                return (
                  <div className='option-group' key={vIdx}>
                    <span className='option-label'>
                      {variant.name}: <span>{getVariantDisplay(selected)}</span>
                    </span>
                    <div className='variant-buttons'>
                      {variant.values.map((val, index) => (
                        <button
                          key={index}
                          className={`variant-btn ${selected === val ? 'active' : ''}`}
                          onClick={() => setVariantSelections((prev) => ({ ...prev, [fieldName]: val }))}
                        >
                          {typeof val === 'object' ? val.name : val}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const productSeo = getProductSEO(product, isArabic);

  return (
    <Wrapper style={{ direction: isArabic ? 'rtl' : 'ltr' }}>
      <SEOHead
        title={productSeo.title}
        description={productSeo.description}
        image={images && images.length > 0 ? images[0]?.url : undefined}
        path={`/products/${id}`}
        type='product'
        product={product}
        lang={isArabic ? 'ar' : 'en'}
      />
      <ProductJsonLd product={product} />
      <BreadcrumbJsonLd items={[
        { name: isArabic ? 'الرئيسية' : 'Home', url: '/' },
        { name: isArabic ? 'المنتجات' : 'Products', url: '/products' },
        { name: name }
      ]} />
      {/* Breadcrumb */}
      <div className='breadcrumb-section'>
        <div className='breadcrumb-container'>
          <nav className='breadcrumb'>
            <Link to='/'>{t.nav.home}</Link>
            <span>/</span>
            <Link to='/products'>{t.nav.products}</Link>
            <span>/</span>
            <span className='current'>{name}</span>
          </nav>
          <Link to='/products' className='back-btn'>
            <FaChevronLeft />
            {t.singleProduct.backToProducts}
          </Link>
        </div>
      </div>

      {/* Image Lightbox Modal */}
      {lightboxOpen && galleryMedia.length > 0 && (
        <div className='lightbox-overlay' onClick={() => setLightboxOpen(false)}>
          <div className='lightbox-container' onClick={(e) => e.stopPropagation()}>
            <button className='lightbox-close' onClick={() => setLightboxOpen(false)}>
              <FaTimes />
            </button>
            <div className='lightbox-body'>
              <div className='lightbox-main'>
                {galleryMedia[lightboxIndex]?.type === 'video' ? (
                  <video
                    src={galleryMedia[lightboxIndex]?.url}
                    className='lightbox-image'
                    controls
                    autoPlay
                    muted
                  />
                ) : (
                  <img
                    src={galleryMedia[lightboxIndex]?.url}
                    alt={`${name} ${lightboxIndex + 1}`}
                    className='lightbox-image'
                  />
                )}
                {galleryMedia.length > 1 && (
                  <>
                    <button
                      className='lightbox-nav lightbox-prev'
                      onClick={() => setLightboxIndex((prev) => (prev - 1 + galleryMedia.length) % galleryMedia.length)}
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className='lightbox-nav lightbox-next'
                      onClick={() => setLightboxIndex((prev) => (prev + 1) % galleryMedia.length)}
                    >
                      <FaChevronRight />
                    </button>
                  </>
                )}
              </div>
              {galleryMedia.length > 1 && (
                <div className='lightbox-thumbs'>
                  {galleryMedia.map((item, idx) => (
                    <div
                      key={idx}
                      className={`lightbox-thumb ${idx === lightboxIndex ? 'active' : ''}`}
                      onClick={() => setLightboxIndex(idx)}
                    >
                      {item.type === 'video' ? (
                        <>
                          <video src={item.url} muted preload='metadata' />
                          <div className='play-overlay'>&#9654;</div>
                        </>
                      ) : (
                        <img src={item.url} alt={`thumb ${idx + 1}`} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Product Section */}
      <div className='product-section'>
        <div className='product-center'>
          {/* Product Images */}
          <div className='product-images'>
            <div className='main-image-container'>
              {mainImage?.isVideo ? (
                <video
                  src={mainImage?.url}
                  className='main-image'
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              ) : (
                <img
                  src={mainImage?.url || images?.[0]?.url}
                  alt={name}
                  className='main-image'
                  onClick={() => {
                    const idx = galleryMedia.findIndex(m => m.url === (mainImage?.url || images?.[0]?.url));
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setLightboxOpen(true);
                  }}
                  style={{ cursor: 'zoom-in' }}
                />
              )}
              {!mainImage?.isVideo && (
                <button
                  className='zoom-btn'
                  onClick={() => {
                    const idx = galleryMedia.findIndex(m => m.url === (mainImage?.url || images?.[0]?.url));
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setLightboxOpen(true);
                  }}
                >
                  <FaSearchPlus />
                </button>
              )}
              <div className='badges'>
                {stock > 0 && <span className='badge'>{t.products.inStock}</span>}
              </div>
            </div>
            {galleryMedia.length > 1 && (
              <div className='thumbnail-gallery'>
                {galleryMedia.map((item, index) => {
                  const isVideo = item.type === 'video';
                  if (isVideo) {
                    return (
                      <div
                        key={`media-${index}`}
                        className={`thumbnail video-thumbnail ${mainImage?.url === item.url && mainImage?.isVideo ? 'active' : ''}`}
                        onClick={() => setMainImage({ url: item.url, isVideo: true })}
                      >
                        <video src={item.url} muted preload='metadata' />
                        <div className='play-overlay'>&#9654;</div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`media-${index}`}
                      className={`thumbnail ${mainImage?.url === item.url && !mainImage?.isVideo ? 'active' : ''}`}
                      onClick={() => setMainImage({ url: item.url })}
                    >
                      <img src={item.url} alt={`${name} ${index + 1}`} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Content */}
          <section className='content'>
            <h1 className='product-title'>{name}</h1>

            <div className='rating-section'>
              <div className='stars'>
                {renderStars(stars || 0)}
              </div>
              <span className='review-count'>
                ({numberOfReviews || 0} {t.singleProduct.reviews})
              </span>
            </div>

            <div className='price-section'>
              <h2 className='price'>{formatPrice(displayPrice)}</h2>
              {isOffer && originalPrice && discountPercentage > 0 && (
                <div className='discount-info'>
                  <span className='original-price'>{formatPrice(originalPrice)}</span>
                  <span className='discount-badge'>-{discountPercentage}%</span>
                </div>
              )}
            </div>

            {/* Benefits Strip */}
            <div className='benefits-strip'>
              {benefits.map((benefit, idx) => (
                <div key={idx} className='benefit-card'>
                  <div className='benefit-icon'>
                    <benefit.icon />
                  </div>
                  <p className='benefit-title'>{benefit.title}</p>
                </div>
              ))}
            </div>



            {stock > 0 ? (
              <>
                {/* Product Options */}
                <div className='product-options'>
                  {hasOffers ? (
                    <div className='option-group offers-group'>
                      <span className='option-label'>
                        {isArabic ? 'اختر العرض' : 'choose your offer'}:
                      </span>
                      <div className='offers-list'>
                        {offers.map((offer, idx) => {
                          const isActive = selectedOffer === idx;
                          const offerQty = offer.quantity || 1;
                          return (
                            <div key={idx} className={`offer-wrapper ${isActive ? 'active' : ''}`}>
                              <label
                                className={`offer-card ${isActive ? 'active' : ''}`}
                                onClick={() => setSelectedOffer(idx)}
                              >
                                <input
                                  type='radio'
                                  name='offer-selection'
                                  checked={isActive}
                                  onChange={() => setSelectedOffer(idx)}
                                />
                                <div className='offer-content'>
                                  <span className='offer-description'>{offer.description}</span>
                                  <div className='offer-pricing'>
                                    <span className='offer-price'>{formatPrice(offer.price)}</span>
                                    <span className='offer-shipping'>
                                      {offer.shippingPrice > 0
                                        ? `+ ${formatPrice(offer.shippingPrice)} ${isArabic ? 'توصيل' : 'livraison'}`
                                        : (isArabic ? 'توصيل مجاني' : 'Livraison gratuite')}
                                    </span>
                                  </div>
                                </div>
                                <div className='offer-check'>
                                  {isActive && <FaCheck />}
                                </div>
                              </label>
                              {/* Render Variants inside active offer if applicable */}
                              {isActive && effectiveVariants.length > 0 && (
                                <div className='offer-internal-variants'>
                                  {renderVariantSelection(offerQty, true)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* No Offers - Standalone Product Options */
                    renderVariantSelection(1, false)
                  )}

                  {/* Quantity */}
                  <div className='option-group'>
                    <span className='option-label'>{t.products.quantity}</span>
                    <div className='quantity-selector'>
                      <button className='qty-btn' onClick={decrease}>−</button>
                      <span className='qty-value'>{amount}</span>
                      <button className='qty-btn' onClick={increase}>+</button>
                    </div>
                  </div>
                </div>

                {/* Customer Information Form */}
                <div className='customer-form'>
                  <h4>{t.customerForm.title}</h4>

                  <div className='form-grid'>
                    <div className='form-row'>
                      <div className='form-group'>
                        <label>{t.customerForm.firstName}</label>
                        <input
                          type='text'
                          name='firstName'
                          value={customerInfo.firstName}
                          onChange={handleCustomerInfoChange}
                          placeholder={t.customerForm.firstName}
                        />
                      </div>
                      <div className='form-group'>
                        <label>{t.customerForm.lastName}</label>
                        <input
                          type='text'
                          name='lastName'
                          value={customerInfo.lastName}
                          onChange={handleCustomerInfoChange}
                          placeholder={t.customerForm.lastName}
                        />
                      </div>
                    </div>

                    <div className='form-group'>
                      <label>{t.customerForm.phone}</label>
                      <input
                        type='tel'
                        name='phone'
                        value={customerInfo.phone}
                        onChange={handleCustomerInfoChange}
                        placeholder={t.customerForm.phonePlaceholder}
                      />
                    </div>

                    <div className='form-row'>
                      <div className='form-group'>
                        <label>{t.customerForm.governorate}</label>
                        <input
                          type='text'
                          name='governorate'
                          value={customerInfo.governorate}
                          onChange={handleCustomerInfoChange}
                          placeholder={t.customerForm.governorate}
                        />
                      </div>
                      <div className='form-group'>
                        <label>{t.customerForm.city}</label>
                        <input
                          type='text'
                          name='city'
                          value={customerInfo.city}
                          onChange={handleCustomerInfoChange}
                          placeholder={t.customerForm.city}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Price Summary Section */}
                <div className='price-summary'>
                  {activeOffer && (
                    <div className='summary-row offer-row'>
                      <span className='label'>{activeOffer.description}</span>
                      <span className='value'>{formatPrice(activeOffer.price)}</span>
                    </div>
                  )}
                  <div className='summary-row'>
                    <span className='label'>{isArabic ? 'المجموع الفرعي' : 'Sous-total'}</span>
                    <span className='value'>{formatPrice(effectivePrice * amount)}</span>
                  </div>
                  <div className='summary-row'>
                    <span className='label'>{isArabic ? 'التوصيل' : 'Livraison'}</span>
                    <span className='value'>
                      {activeOffer
                        ? (activeOffer.shippingPrice > 0
                          ? formatPrice(activeOffer.shippingPrice)
                          : (isArabic ? 'مجاني' : 'Gratuit'))
                        : (getShippingCost(product.shipping) > 0 && (displayPrice * amount) < 200
                          ? formatPrice(getShippingCost(product.shipping))
                          : (isArabic ? 'مجاني' : 'Gratuit'))}
                    </span>
                  </div>
                  <div className='summary-divider'></div>
                  <div className='summary-row total'>
                    <span className='label'>{isArabic ? 'المجموع' : 'Total'}</span>
                    <span className='value'>
                      {formatPrice(
                        (effectivePrice * amount) +
                        (activeOffer
                          ? activeOffer.shippingPrice
                          : (getShippingCost(product.shipping) > 0 && (displayPrice * amount) < 200 ? getShippingCost(product.shipping) : 0))
                      )}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className='action-buttons'>
                  <button
                    className='order-now-btn'
                    onClick={handleCashOnDelivery}
                    disabled={orderLoading}
                  >
                    {orderLoading ? t.common.loading : t.singleProduct.orderNow}
                  </button>
                  <button
                    className='add-cart-btn'
                    onClick={handleAddToCart}
                  >
                    {t.singleProduct.addToCart}
                  </button>
                </div>

                {/* Product Specifications (Rich Text) */}
                <div className='product-specs' style={{ marginTop: '2rem' }}>
                  <div className='specs-header'>
                    <div className='specs-icon'>
                      <FaCheck />
                    </div>
                    <h3 className='specs-title'>
                      {isArabic ? 'مواصفات المنتج' : 'Caractéristiques'}
                    </h3>
                  </div>
                  <div className='spec-list'>
                    <div className='spec-item rich-text-description' style={{ borderBottom: 'none', padding: 0 }}>
                      <div
                        className='desc-html'
                        dir={isArabicText(description) ? 'auto' : 'ltr'}
                        style={!isArabicText(description) ? { textAlign: 'left' } : {}}
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className='out-of-stock-message'>
                {t.products.outOfStock}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Why Choose Darista Section */}
      <section className='why-choose-section'>
        <div className='why-choose-container'>
          <div className='section-header'>
            <span className='section-badge'>
              {isArabic ? 'لماذا داريستا؟' : 'Pourquoi Darista ?'}
            </span>
            <h2 className='section-title'>
              {isArabic ? 'ما يميزنا' : 'Ce qui nous rend'} <span>{isArabic ? '' : 'différents'}</span>
            </h2>
          </div>

          <div className='why-choose-grid'>
            {whyChooseReasons.map((reason, idx) => (
              <div key={idx} className='reason-card'>
                <div className='reason-icon'>
                  <reason.icon />
                </div>
                <h3 className='reason-title'>{reason.title}</h3>
                <p className='reason-description'>{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      < section className='reviews-section' >
        <div className='reviews-container'>
          <div className='reviews-header'>
            <div>
              <span className='section-badge' style={{ background: 'rgba(201, 169, 98, 0.1)', color: '#C9A962' }}>
                {isArabic ? 'آراء العملاء' : 'Avis Clients'}
              </span>
              <h3>
                {isArabic ? 'ماذا يقول' : 'Ce que disent nos'} <span>{isArabic ? 'عملاؤنا' : 'clients'}</span>
              </h3>
            </div>

            <div className='reviews-summary'>
              <div className='rating-block'>
                <div className='stars'>
                  {renderStars(stars || 0)}
                </div>
                <p className='rating-value'>{stars?.toFixed(1) || '0.0'}/5</p>
                <p className='review-count'>
                  {numberOfReviews || 0} {t.singleProduct.reviews}
                </p>
              </div>
              <ReviewModal product={product} />
            </div>
          </div>

          {reviews.length > 0 ? (
            <div className='reviews-grid'>
              {reviews.map((review, index) => (
                <div key={index} className='review-card'>
                  <FaQuoteRight className='quote-icon' />
                  <div className='review-author'>
                    <img
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(review.name || 'User')}&background=6b7b5c&color=fff`}
                      alt={review.name}
                    />
                    <div className='author-info'>
                      <h4>
                        {review.name}
                        <span className='verified-badge'>
                          <FaCheck /> {isArabic ? 'موثق' : 'Vérifié'}
                        </span>
                      </h4>
                      <div className='author-rating'>
                        <div className='stars'>
                          {renderStars(review.rating || 5)}
                        </div>
                        <span className='date'>
                          {review.createdAt
                            ? new Date(review.createdAt).toLocaleDateString()
                            : ''
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className='review-text'>{review.comment}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className='no-reviews'>{t.singleProduct.noReviews}</p>
          )}
        </div>
      </section>

      {/* Mobile Sticky CTA */}
      {
        stock > 0 && (
          <div className='mobile-sticky-cta'>
            <div className='sticky-content'>
              <span className='sticky-price'>
                {formatPrice(
                  (effectivePrice * amount) +
                  (activeOffer
                    ? activeOffer.shippingPrice
                    : (getShippingCost(product.shipping) > 0 && (displayPrice * amount) < 200 ? getShippingCost(product.shipping) : 0))
                )}
              </span>
              <button
                className='sticky-btn'
                onClick={() => document.querySelector('.customer-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t.singleProduct.orderNow}
              </button>
            </div>
          </div>
        )
      }
    </Wrapper >
  );
};

export default SingleProductPage;
